import {
    CaptchaContextState,
    useCaptcha,
} from '@root/components/CaptchaProvider';
import { SelectOption } from '@root/components/Form/Select';
import { useLiveHelp } from '@root/components/LiveHelpContext';
import { PageComponent } from '@root/pages/Type';
import { Router as i18nRouter } from '@root/router';
import {
    RegistrationExtraValues,
    RegistrationFormValues,
    RegistrationResponse,
    userAuthRegister,
} from '@root/store/auth';
import { listReset } from '@root/store/services';
import { AppDispatch, RootState } from '@root/store/types';
import { RegistrationSchemaInit } from '@root/utils/ValidationSchemas/Index';
import { ServerData } from '@root/utils/axios';
import PrettyError from '@root/utils/errors/PrettyError';
import useRouteHref from '@root/utils/useRouteHref';
import withForm, { FormHandlerInside, withFormProps } from '@utils/withForm';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import Content from './Content';

export const initialValues: RegistrationFormValues = {
    email: '',
    password: '',
    recaptcha: '',
    country: 0,
    newsletter: true,
};

export type ContainerProps = withFormProps<RegistrationFormValues> & {
    countries: SelectOption[];
    currentLanguage: string | null;
    handleUserAuthRegister: (
        values: RegistrationFormValues & RegistrationExtraValues,
        captcha?: CaptchaContextState,
    ) => Promise<ServerData<RegistrationResponse>>;
};

export type RegistrationOptionsResponseData = ServerData & {
    securityquestions: SelectOption[];
    countries: SelectOption[];
    phonecodes: SelectOption[];
    selectedCountry: number | null;
};

const Container: PageComponent<ContainerProps> = ({
    currentLanguage,
    handleForm,
    submitting,
    isValid,
    errorMessage,
    handleUserAuthRegister,
    countries,
}) => {
    const { publicRuntimeConfig } = getConfig();
    const returnToLogin = '';
    const router = useRouter();

    const { t } = useTranslation('userRegistration');
    const captcha = useCaptcha();

    const redirectUrl = useRouteHref('registration_confirm');
    const orderUrl = useRouteHref('ui_order');
    const dispatch = useDispatch<AppDispatch>();

    const { wantHide } = useLiveHelp();

    const { setVisible } = captcha;
    useEffect(() => {
        i18nRouter.prefetchRoute('ui_services_list', currentLanguage);

        setVisible(true);
        wantHide(true);

        return () => {
            setVisible(false);
            wantHide(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formSubmit: FormHandlerInside<RegistrationFormValues> = ({
        values,
    }) => {
        return handleUserAuthRegister(
            {
                ...values,
                redirectUrl: publicRuntimeConfig.HOST_URL + redirectUrl,
                orderUrl: publicRuntimeConfig.HOST_URL + orderUrl,
            },
            captcha,
        ).then((response) => {
            if (response.emailUsing) {
                throw new PrettyError(
                    t('form.errors.emailUsing', {
                        defaultValue:
                            'El. pašto adresas arba vartotojo vardas jau naudojamas. Prašome pasirinkti kitą.',
                    }),
                );
            }
            if (response.refNotFund) {
                throw new PrettyError(
                    t('form.errors.refNotFound', {
                        defaultValue:
                            'Jūs mėginate naudoti neveikiantį referalą. Prašome pašalinti arba naudoti kitą.',
                    }),
                );
            }
            if (!response.success) {
                throw new PrettyError(
                    t('form.errors.fatalError', {
                        defaultValue:
                            'Nepavyko užregistruoti. Prašome mėginti dar kartą.',
                    }),
                );
            }
            dispatch(listReset());
            if (returnToLogin) {
                return router.push(returnToLogin);
            }
            return i18nRouter.pushRoute(
                'ui_services_list',
                {},
                currentLanguage,
            );
        });
    };

    return (
        <Content
            onSubmitForm={handleForm(formSubmit)}
            submitting={submitting}
            isValid={isValid}
            errorMessage={errorMessage}
            countries={countries}
        />
    );
};

const mapStateToProps = (state: RootState) => ({
    currentLanguage: state.i18n.currentLanguage,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
    handleUserAuthRegister: (
        values: RegistrationFormValues & RegistrationExtraValues,
        captcha?: CaptchaContextState,
    ) => dispatch(userAuthRegister(values, captcha)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(
    withForm({
        initialValues,
        validationSchema: () => RegistrationSchemaInit(),
    })(Container),
);
